import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Grid, Typography, Input, DatePicker, Tag } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { useGetAllMentorshipMasterMutation, useGetAllMentorshipMasterBySearchMutation, useSearchMentorshipMasterByDateRangeMutation, useExportMentorshipMutation } from '../../redux-toolkit/services/mentorshipMasterService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';
import moment from 'moment';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const MentorshipDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    // Local filters
    const [searchText, setSearchText] = useState('');
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isDateSearchMode, setIsDateSearchMode] = useState(false);

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();
    const [getAllMentorshipMaster, { data: mentorshipData, isLoading: isMentorshipLoading }] = useGetAllMentorshipMasterMutation();
    const [getAllMentorshipMasterBySearch, { data: mentorshipSearchData, isLoading: isMentorshipSearchLoading }] = useGetAllMentorshipMasterBySearchMutation();
    const [searchMentorshipMasterByDateRange, { data: mentorshipDateSearchData, isLoading: isMentorshipDateSearchLoading }] = useSearchMentorshipMasterByDateRangeMutation();

    const [exportMentorship, { isLoading: isExporting }] = useExportMentorshipMutation();

    // EXPORT MUTATION AND FUNCTION
    const handleExportCsv = async () => {
        try {
            const response: any = await exportMentorship({
                token,
                payload: {
                    offset: 1000,
                    pageNumber: 0,
                    searchBy: searchText || ""
                }
            }).unwrap();

            const url = window.URL.createObjectURL(new Blob([response]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'mentorship-sessions.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    useEffect(() => {
        if (token && !searchText && !isDateSearchMode) {
            getAllMentorshipMaster({
                token,
                page: currentPage - 1,
                size: pageSize,
                orderBy: "desc",
            });
        }
    }, [token, currentPage, pageSize, searchText, isDateSearchMode]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        if (value) {
            getAllMentorshipMasterBySearch({
                token,
                payload: { offset: 1000, pageNumber: 0, searchBy: value },
            });
        }
    };

    const handleDateSearch = () => {
        if (startDate && endDate) {
            setIsDateSearchMode(true);
            searchMentorshipMasterByDateRange({
                token,
                payload: {
                    fromDate: startDate.format("YYYY-MM-DDTHH:mm:ss"),
                    toDate: endDate.format("YYYY-MM-DDTHH:mm:ss"),
                    pageNumber: currentPage - 1,
                    offset: pageSize
                }
            });
        }
    };

    const resetDateSearch = () => {
        setStartDate(null);
        setEndDate(null);
        setIsDateSearchMode(false);
    };

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // -- Data for Mentorship Sessions --
    let apiData = mentorshipData?.data;
    let totalRecords = mentorshipData?.totalRecords || 0;

    if (searchText) {
        apiData = mentorshipSearchData?.data;
        totalRecords = mentorshipSearchData?.totalRecords || 0;
    } else if (isDateSearchMode) {
        apiData = mentorshipDateSearchData?.data;
        totalRecords = mentorshipDateSearchData?.totalRecords || 0;
    }

    const filteredSessions = Array.isArray(apiData) ? apiData.map((item: any) => ({
        key: item.id,
        heir: item.entityMentee?.name || '-',
        mentor: item.entityMentor?.name || '-',
        fromDate: item.fromDate ? item.fromDate.split('T')[0] : '-',
        toDate: item.toDate ? item.toDate.split('T')[0] : '-',
        hours: item.hours || 0,
        topic: item.topic || '-',
        notes: item.notes || '—'
    })) : [];

    // Derived Metrics
    const totalHours = filteredSessions.reduce((acc, curr) => acc + Number(curr.hours), 0);
    const uniqueHeirs = new Set(filteredSessions.map(s => s.heir)).size;
    const uniqueMentors = new Set(filteredSessions.map(s => s.mentor)).size;
    const avgSession = (totalHours / (filteredSessions.length || 1)).toFixed(1);

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        PASSIONIT — Reliance Family Office
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Mentorship</span>
                    </div>
                </div>
                {/* <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div> */}
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - Mentorship Table & Metrics */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <Title level={4} className="mb-4">Mentorship & Mentor Management</Title>

                        <div className="d-flex gap-2 mb-4">
                            <Input
                                prefix={<SearchOutlined />}
                                placeholder="Search mentor, heir, or topic"
                                style={{ width: 240 }}
                                onChange={handleSearch}
                            />
                            <DatePicker placeholder="dd/mm/yyyy" value={startDate} onChange={(date) => setStartDate(date)} style={{ width: 140 }} />
                            <DatePicker placeholder="dd/mm/yyyy" value={endDate} onChange={(date) => setEndDate(date)} style={{ width: 140 }} />
                            <Button type="primary" onClick={handleDateSearch} style={{ backgroundColor: '#0078d4' }}>Search Time</Button>
                            {isDateSearchMode && <Button type="default" onClick={resetDateSearch}>Reset</Button>}
                            <Button type="default" className="bg-success text-white border-success" onClick={handleExportCsv} loading={isExporting}>Export CSV</Button>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {filteredSessions.length} of {totalRecords} mentorship sessions</Text>

                        <Table
                            dataSource={filteredSessions}
                            pagination={{
                                total: totalRecords,
                                current: currentPage,
                                pageSize: pageSize,
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                }
                            }}
                            loading={isMentorshipLoading || isMentorshipSearchLoading || isMentorshipDateSearchLoading}
                            columns={[
                                { title: 'HEIR', dataIndex: 'heir', key: 'heir', render: (text) => <b>{text}</b> },
                                { title: 'MENTOR', dataIndex: 'mentor', key: 'mentor', render: (text) => <Text>{text}</Text> },
                                { title: 'FROM DATE', dataIndex: 'fromDate', key: 'fromDate', render: (text) => <Text>{text}</Text> },
                                { title: 'TO DATE', dataIndex: 'toDate', key: 'toDate', render: (text) => <Text>{text}</Text> },
                                { title: 'HOURS', dataIndex: 'hours', key: 'hours', render: (text) => <b>{text}h</b> },
                                { title: 'TOPIC', dataIndex: 'topic', key: 'topic', render: (text) => <Text>{text}</Text> },
                                { title: 'NOTES', dataIndex: 'notes', key: 'notes', render: (text) => <Text type="secondary">{text}</Text> }
                            ]}
                        />

                        {/* Summary Metrics */}
                        <Row gutter={16} className="mt-4">
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f0f5ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Total Hours</Text>
                                    <Title level={3} className="text-primary m-0">{totalHours}h</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f6ffed' }}>
                                    <Text type="secondary" className="d-block mb-1">Unique Heirs</Text>
                                    <Title level={3} className="text-success m-0">{uniqueHeirs}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f9f0ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Unique Mentors</Text>
                                    <Title level={3} style={{ color: '#722ed1', margin: 0 }}>{uniqueMentors}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fff7e6' }}>
                                    <Text type="secondary" className="d-block mb-1">Avg Session</Text>
                                    <Title level={3} style={{ color: '#fa8c16', margin: 0 }}>{avgSession}h</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default MentorshipDashboard;
